import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import type { MetricDefinition, ReportRun, ReportSnapshot } from '../types/domain';

/**
 * Report export (FASE 12 closure-correction pass). Every export below is a
 * pure function of the caller-supplied `report`/`snapshots` — the exact
 * immutable rows already fetched from `report_runs`/`report_snapshots` —
 * never of browser DOM state. `buildCanonicalReportPayload` is the single
 * shared source both CSV and PDF are built from, and
 * `computeReportChecksum` hashes that canonical (metric_code, computed_value)
 * data — not PDF/CSV file bytes, which legitimately differ between renders
 * (a fresh `generated_at` timestamp is embedded in every render) — so two
 * independent exports of the SAME report_run_id always produce an IDENTICAL
 * checksum, proving the underlying data is reproduced exactly, even though
 * the file bytes themselves are not required to be byte-identical.
 */

export interface CanonicalMetricRow {
  metric_code: string;
  name: string;
  domain: string;
  unit: string;
  computed_value: number;
}

export interface CanonicalReportPayload {
  report_run_id: string;
  report_type: string;
  period_start: string;
  period_end: string;
  branch_id: string | null;
  status: string;
  freshness_note: string | null;
  metrics: CanonicalMetricRow[];
}

export function buildCanonicalReportPayload(
  report: ReportRun,
  snapshots: ReportSnapshot[],
  metricByCode: Map<string, MetricDefinition>
): CanonicalReportPayload {
  const metrics = snapshots
    .map((s) => {
      const def = metricByCode.get(s.metric_code);
      return {
        metric_code: s.metric_code,
        name: def?.name ?? s.metric_code,
        domain: def?.source_domain ?? '',
        unit: def?.unit ?? '',
        computed_value: s.computed_value,
      };
    })
    .sort((a, b) => a.metric_code.localeCompare(b.metric_code));

  return {
    report_run_id: report.report_run_id,
    report_type: report.report_type,
    period_start: report.period_start,
    period_end: report.period_end,
    branch_id: report.branch_id,
    status: report.status,
    freshness_note: report.freshness_note,
    metrics,
  };
}

/** SHA-256 of the canonical (non-timestamped) payload — stable across repeated exports of the same immutable report_run. */
export async function computeReportChecksum(payload: CanonicalReportPayload): Promise<string> {
  const canonical = JSON.stringify({
    report_run_id: payload.report_run_id,
    report_type: payload.report_type,
    period_start: payload.period_start,
    period_end: payload.period_end,
    branch_id: payload.branch_id,
    status: payload.status,
    metrics: payload.metrics.map((m) => [m.metric_code, m.computed_value]),
  });
  const bytes = new TextEncoder().encode(canonical);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function buildReportCsv(payload: CanonicalReportPayload): string {
  const header = ['metric_code', 'name', 'domain', 'unit', 'computed_value'];
  const lines = [header.join(',')];
  for (const m of payload.metrics) {
    const row = [m.metric_code, m.name, m.domain, m.unit, String(m.computed_value)];
    lines.push(row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
  }
  return lines.join('\n');
}

const REPORT_TYPE_LABEL: Record<string, string> = { DAILY: 'Laporan Harian', MONTHLY: 'Laporan Bulanan' };

/**
 * pdf-lib's standard fonts (Helvetica/HelveticaBold) only support the
 * WinAnsi (CP1252) code page — a real character outside it (an emoji, most
 * CJK, etc. — confirmed live: a metric/branch name containing one throws
 * "WinAnsi cannot encode ...") would crash the whole export. Metric names
 * and branch labels are TENANT_OWNER/org-authored free text, not
 * hand-picked by this codebase, so this is a genuine future robustness
 * gap, not a hypothetical — replacing an unencodable character with '?'
 * keeps the export succeeding (never a fake/corrupt "success", just a
 * graceful degradation of that one glyph) rather than failing the whole
 * report for a single unsupported character.
 */
function sanitizeForWinAnsi(text: string): string {
  return Array.from(text)
    .map((ch) => (ch.codePointAt(0)! <= 0xff ? ch : '?'))
    .join('');
}

/**
 * Real PDF binary generation via pdf-lib (no browser print dialog, no
 * mocked values) — the previous pass's `window.print()` placeholder did not
 * qualify as a real generated artifact and has been replaced entirely.
 */
export async function buildReportPdf(payload: CanonicalReportPayload, branchLabel: string, generatedAt: Date): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  let page = doc.addPage([595.28, 841.89]); // A4
  const margin = 48;
  let y = 841.89 - margin;
  const lineHeight = 16;

  const drawText = (text: string, opts: { size?: number; useBold?: boolean; color?: [number, number, number] } = {}) => {
    const size = opts.size ?? 10;
    if (y < margin + lineHeight) {
      page = doc.addPage([595.28, 841.89]);
      y = 841.89 - margin;
    }
    page.drawText(sanitizeForWinAnsi(text), {
      x: margin,
      y,
      size,
      font: opts.useBold ? bold : font,
      color: opts.color ? rgb(...opts.color) : rgb(0.1, 0.1, 0.1),
    });
    y -= size + 6;
  };

  drawText(`ALEX ERP — ${REPORT_TYPE_LABEL[payload.report_type] ?? payload.report_type}`, { size: 16, useBold: true });
  y -= 4;
  const periodLabel = payload.period_start === payload.period_end ? payload.period_start : `${payload.period_start} s/d ${payload.period_end}`;
  drawText(`Periode: ${periodLabel}`, { size: 11 });
  drawText(`Cabang: ${branchLabel}`, { size: 11 });
  drawText(`Status: ${payload.status}${payload.freshness_note ? ' — ' + payload.freshness_note : ''}`, { size: 11 });
  drawText(`report_run_id: ${payload.report_run_id}`, { size: 9, color: [0.4, 0.4, 0.4] });
  drawText(`Dibuat pada: ${generatedAt.toISOString()}`, { size: 9, color: [0.4, 0.4, 0.4] });
  y -= 10;

  if (payload.metrics.length === 0) {
    drawText('Tidak ada metrik yang terlihat untuk laporan ini.', { size: 10 });
  } else {
    drawText('Metrik', { size: 10, useBold: true });
    for (const m of payload.metrics) {
      const valueText = m.unit === 'IDR' ? `Rp ${m.computed_value.toLocaleString('id-ID')}` : m.computed_value.toLocaleString('id-ID');
      drawText(`${m.name} (${m.domain}) — ${valueText}${m.unit && m.unit !== 'IDR' ? ' ' + m.unit : ''}`, { size: 9.5 });
    }
  }

  return doc.save();
}

export function downloadBytes(filename: string, bytes: Uint8Array, mime: string) {
  const blob = new Blob([bytes as unknown as BlobPart], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadText(filename: string, content: string, mime: string) {
  downloadBytes(filename, new TextEncoder().encode(content), mime);
}
